import { Button, Input, Space, Typography } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import type { LinkEntry } from '@crm/shared';

const href = (url: string) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

/** Stored LINKS value as entries; tolerates a plain string from older data. */
export function toLinks(value: unknown): LinkEntry[] {
  if (Array.isArray(value)) return value.filter((x) => x && typeof x === 'object' && 'url' in x) as LinkEntry[];
  if (typeof value === 'string' && value.trim()) return [{ url: value.trim(), label: null }];
  return [];
}

/** Links with their labels, e.g. "LinkedIn  linkedin.com/in/ada". */
export function LinksView({ value, inline }: { value: unknown; inline?: boolean }) {
  const links = toLinks(value);
  if (!links.length) return null;
  const items = links.map((l, i) => (
    <span key={i}>
      {l.label && <Typography.Text type="secondary">{l.label}: </Typography.Text>}
      <a href={href(l.url)} target="_blank" rel="noreferrer">
        {l.url.replace(/^https?:\/\/(www\.)?/i, '')}
      </a>
    </span>
  ));
  if (inline) return <span>{items.reduce<React.ReactNode[]>((acc, x, i) => (i ? [...acc, ', ', x] : [x]), [])}</span>;
  return <Space direction="vertical" size={2}>{items}</Space>;
}

/** Editable list of label + URL rows. Receives value/onChange from Form.Item. */
export function LinksInput({ value, onChange, disabled }: { value?: unknown; onChange?: (v: LinkEntry[]) => void; disabled?: boolean }) {
  const links = toLinks(value);
  const set = (next: LinkEntry[]) => onChange?.(next);
  return (
    <Space direction="vertical" size={6} style={{ width: '100%' }}>
      {links.map((l, i) => (
        <Space.Compact key={i} style={{ width: '100%' }}>
          <Input
            style={{ width: '35%' }}
            placeholder="Label"
            value={l.label ?? ''}
            disabled={disabled}
            onChange={(e) => set(links.map((x, j) => (j === i ? { ...x, label: e.target.value || null } : x)))}
          />
          <Input
            placeholder="https://"
            value={l.url}
            disabled={disabled}
            onChange={(e) => set(links.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))}
          />
          <Button icon={<MinusCircleOutlined />} aria-label="Remove link" title="Remove link" disabled={disabled} onClick={() => set(links.filter((_, j) => j !== i))} />
        </Space.Compact>
      ))}
      <Button type="dashed" size="small" icon={<PlusOutlined />} disabled={disabled} onClick={() => set([...links, { url: '', label: null }])}>
        Add link
      </Button>
    </Space>
  );
}
